import { db, FieldValue } from 'utils/firebase';
import server from 'utils/server';
import {
  CAPACITY_EPOCH_COLLECTION,
  CAPACITY_FIELDS,
  capacityEpochId,
} from 'types/shared/helper/reservationCapacity';
import {
  hasConfirmationHistory,
  lifecycleDocument,
  lifecycleUpdate,
  reservationPhase,
  touchesPhase,
  withoutLifecycle,
} from 'types/shared/helper/reservationLifecycle';
import { RequestStatus } from 'types/shared/helper/reservations';

const serverTimestamp = () => FieldValue.serverTimestamp();

// Dashboard writers on the shared capacity protocol. Only for a restaurant
// whose switch (restaurantData.flags.reservationCapacityProtocol) is on: then
// a write that occupies, frees or moves capacity advances the day counter in
// the same batch or transaction, so the reservation API's capacity check sees
// it, and the Firestore rules refuse such writes without the counter. With
// the switch off nothing changes: no counter is written (rules without the
// counter collection keep working).
//
// Two kinds of placement:
// - checked: staff took a free time and its tables from the availability list.
//   On the protocol the list came with the day counter the server read before
//   computing it (`capacityExpectation`); the write sets the counter to exactly
//   that value plus one, which the rules (and the API's own claims) only accept
//   while nobody changed the day in between. A checked placement without that
//   stamp (list fetched before the switch came on) is never committed as if it
//   were an override: PlacementOutdated, check again.
// - explicit: a walk-in, tables kept or picked by hand, or a full time chosen
//   with the PIN-protected `reservations.blockedTime.write` right. The counter
//   just advances; staff override the availability on purpose.

type Patch = Record<string, unknown>;
type Writer = { set: (ref: any, data: any, options?: any) => unknown };
type Reader = { get: (ref: any) => Promise<{ data: () => any }> };

/** The day counter the server read before the availability a placement was picked from. */
export type CapacityExpectation = { date: string; epoch: number };

/** How a slot/table change came about (see above); absent for other changes. */
export type Placement =
  | { checked: true; expectation: CapacityExpectation | null | undefined }
  | { checked: false };

/** The availability of this day changed after it was shown: check the time again. */
export class PlacementOutdated extends Error {
  constructor() {
    super('Die Verfügbarkeit dieses Tages hat sich geändert. Bitte prüfe die Uhrzeit erneut.');
  }
}

/** The reservation changed since the form was opened: reload before saving. */
export class ReservationChanged extends Error {
  constructor() {
    super('Diese Reservierung wurde inzwischen geändert. Bitte lade sie neu.');
  }
}

const CAPACITY = CAPACITY_FIELDS as readonly string[];

export const touchesCapacity = (patch: Patch) =>
  Object.keys(patch).some((path) => CAPACITY.includes(path.split('.')[0]));

const valueAt = (data: Record<string, any>, path: string) =>
  path.split('.').reduce((value: any, key) => (value == null ? value : value[key]), data);

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const counterRef = (restaurantId: string, date: string) =>
  db.collection(CAPACITY_EPOCH_COLLECTION).doc(capacityEpochId(restaurantId, date));

const restaurantDataRef = (restaurantId: string) => db.collection('restaurantData').doc(restaurantId);

const switchOn = (restaurantData: any) => restaurantData?.flags?.reservationCapacityProtocol === true;

/**
 * Whether the restaurant is on the protocol, read fresh. Inside a transaction
 * pass it as `reader`; a batch reads it right before committing (if the
 * switch comes on in between, the rules refuse the batch: nothing is lost).
 */
export async function capacityProtocolOn(restaurantId: string, reader?: Reader) {
  const ref = restaurantDataRef(restaurantId);
  const snapshot = reader ? await reader.get(ref) : await (ref as any).get();
  return switchOn(snapshot.data());
}

/**
 * Advances the counters of the given days in the same batch or transaction
 * (call only when the restaurant is on the protocol); the day of a checked
 * placement to exactly its expected value plus one.
 */
export function advanceDayCounters(
  writer: Writer,
  restaurantId: string,
  dates: (string | null | undefined)[],
  placement?: Placement
) {
  if (placement?.checked && !placement.expectation) throw new PlacementOutdated();
  const expectation = placement?.checked ? placement.expectation : null;
  Array.from(new Set(dates.filter((date): date is string => !!date))).forEach((date) =>
    writer.set(
      counterRef(restaurantId, date),
      {
        restaurantId,
        date,
        epoch:
          expectation?.date === date ? expectation.epoch + 1 : FieldValue.increment(1),
        touchedAt: FieldValue.serverTimestamp(),
      },
      { merge: true }
    )
  );
}

/** A refused write of a checked placement: the counter moved, so the slot must be checked again. */
const outdatedWhenRefused = (placement?: Placement) => (error: any) => {
  if (placement?.checked && error?.code === 'permission-denied') throw new PlacementOutdated();
  throw error;
};

/**
 * Applies a dashboard patch to a reservation. A technical patch stays a plain
 * update. A patch that changes capacity, or may confirm the booking, runs as a
 * transaction that reads the reservation (and the restaurant's switch), so on
 * the protocol the counter of the day it is on now (and of a new day) advances
 * with it, and a first confirmation carries the lifecycle stamp
 * (types/shared/helper/reservationLifecycle.ts). With `expected`, the patch
 * applies only while those fields still hold the given values: an undo or an
 * edit never overwrites a change made since. Resolves to false when an
 * expectation no longer holds.
 */
export async function updateReservation(
  id: string,
  patch: Patch,
  expected?: Patch,
  placement?: Placement
) {
  const ref = db.collection('requestsV03').doc(id);
  if (!expected && !touchesCapacity(patch) && !touchesPhase(patch)) {
    await ref.update(withoutLifecycle(patch));
    return true;
  }
  const current = (await ref.get()).data();
  if (current?.restaurant && (current.visitWishStaffConversion || await usesAllocationApi(current.restaurant))) {
    try {
      await postWrites(current.restaurant, {
        writes: [{ op: 'update', id, data: patch, ...(expected ? { expected } : {}) }],
        ...(placement ? { placement } : {}),
      });
      return true;
    } catch (error) {
      const code = allocationErrorCode(error);
      if (code === 'RESERVATION_CHANGED') return false;
      if (code === 'PLACEMENT_OUTDATED') throw new PlacementOutdated();
      throw error;
    }
  }
  return db
    .runTransaction(async (transaction) => {
      const current = (await transaction.get(ref)).data();
      if (!current) throw new Error('Reservation not found');
      const protocolOn = touchesCapacity(patch) && (await capacityProtocolOn(current.restaurant, transaction));
      if (expected && Object.entries(expected).some(([path, value]) => !same(valueAt(current, path), value))) {
        return false;
      }
      transaction.update(ref, lifecycleUpdate(current, patch, serverTimestamp));
      if (protocolOn) {
        advanceDayCounters(
          transaction,
          current.restaurant,
          [current.date, typeof patch.date === 'string' ? patch.date : current.date],
          placement
        );
      }
      return true;
    })
    .catch(outdatedWhenRefused(placement));
}

/** A reservation document with the same update() call sites already use, on the protocol. */
export const reservationDocument = (id: string) => ({
  ref: db.collection('requestsV03').doc(id),
  update: (patch: Patch, expected?: Patch) => updateReservation(id, patch, expected),
});

/**
 * Creates a reservation document (with its day counter on the protocol). An
 * existing id is only reused for the flow's own placeholder or another hold
 * (still held or expired) or a failed booking; a live booking is never
 * replaced (ReservationChanged). The stored lifecycle of a reused id is kept;
 * a booking written confirmed is stamped as its first confirmation.
 */
export async function writeReservationDocument(
  data: Record<string, any>,
  id?: string,
  placement?: Placement
) {
  const ref = id ? db.collection('requestsV03').doc(id) : db.collection('requestsV03').doc();
  if (await usesAllocationApi(data.restaurant)) {
    try {
      const [created] = await postWrites(data.restaurant, {
        writes: [{ op: 'set', id: ref.id, data, reusableOnly: true }],
        ...(placement ? { placement } : {}),
      });
      return created;
    } catch (error) {
      const code = allocationErrorCode(error);
      if (code === 'RESERVATION_CHANGED') throw new ReservationChanged();
      if (code === 'PLACEMENT_OUTDATED') throw new PlacementOutdated();
      throw error;
    }
  }
  await db
    .runTransaction(async (transaction) => {
      const current = (await transaction.get(ref)).data();
      const protocolOn = await capacityProtocolOn(data.restaurant, transaction);
      if (current && current.status !== 'failed' && !((current.validTill ?? 0) > 0)) {
        throw new ReservationChanged();
      }
      transaction.set(ref, lifecycleDocument(current, data, serverTimestamp));
      if (protocolOn) {
        advanceDayCounters(transaction, data.restaurant, [current?.date, data.date], placement);
      }
    })
    .catch(outdatedWhenRefused(placement));
  return ref.id;
}

/** The waiting-list request was accepted, cancelled or changed elsewhere. */
export class WaitingListEntryChanged extends Error {
  constructor() {
    super('This request was changed in the meantime.');
  }
}

/**
 * Fields a placeholder hold is identified by besides its whole guest;
 * `validTill` (ms) is unique per placeholder written.
 */
const PLACEHOLDER_FIELDS = [
  'restaurant',
  'status',
  'validTill',
  'date',
  'time',
  'startTimeInBlocks',
  'endTimeInBlocks',
  'guests',
  'occassion',
  'tables',
  'space',
  'reservationLength',
];

/** Deep equality independent of map key order (Firestore does not keep it). */
const canonical = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(canonical)
    : value && typeof value === 'object'
      ? Object.fromEntries(
          Object.keys(value as object)
            .sort()
            .map((key) => [key, canonical((value as Record<string, unknown>)[key])])
        )
      : value;
const sameValue = (a: unknown, b: unknown) => same(canonical(a), canonical(b));

/**
 * Whether the stored document is still a dashboard placeholder hold nobody
 * turned into a booking: its whole guest still exactly the placeholder guest
 * it was written with (a name, e-mail, phone or any other guest field entered
 * meanwhile makes it someone's booking in progress), still a hold, never
 * confirmed before, and, given the copy the flow wrote, unchanged in every
 * field that identifies that very placeholder.
 */
export function isUntouchedPlaceholder(
  stored: Record<string, any> | undefined,
  placeholder: { guest: Record<string, any>; written?: Record<string, any> }
) {
  if (!stored || !sameValue(stored.guest, placeholder.guest)) return false;
  if (!((stored.validTill ?? 0) > 0) || reservationPhase(stored) !== 'unconfirmed' || hasConfirmationHistory(stored)) {
    return false;
  }
  const written = placeholder.written;
  return !written || PLACEHOLDER_FIELDS.every((path) => same(valueAt(stored, path), valueAt(written, path)));
}

/**
 * Turns the placeholder hold the waiting-list flow just wrote into the
 * booking and closes the request, in one transaction that reads both first:
 * the request must still be pending (WaitingListEntryChanged) and the
 * reservation still that untouched placeholder (ReservationChanged), so a
 * concurrent confirmation, guest entry, move or deletion is never
 * overwritten. The patch and the first-confirmation stamp are derived from
 * the stored reservation; on the protocol the counter of its stored day
 * advances in the same commit (ending a hold is a capacity change; staff
 * place a waiting-list guest on purpose: an explicit placement).
 */
export async function finalizeWaitingListReservation(options: {
  reservationId: string;
  entryId: string;
  /** The placeholder exactly as the flow wrote it. */
  placeholder: Record<string, any>;
  /** guest, validTill: 0 and bookkeeping of the confirmation. */
  confirm: Patch;
  entryPatch: Patch;
}) {
  const ref = db.collection('requestsV03').doc(options.reservationId);
  const entryRef = db.collection('waitinglistEntries').doc(options.entryId);
  const identity = { guest: options.placeholder.guest ?? {}, written: options.placeholder };
  await db.runTransaction(async (transaction) => {
    const entry = (await transaction.get(entryRef)).data();
    const stored = (await transaction.get(ref)).data();
    const protocolOn = await capacityProtocolOn(options.placeholder.restaurant, transaction);
    if (entry?.status !== RequestStatus.PENDING) throw new WaitingListEntryChanged();
    // The confirmation replaces the whole guest: any guest field changed meanwhile refuses it.
    if (!isUntouchedPlaceholder(stored, identity)) throw new ReservationChanged();
    transaction.update(ref, lifecycleUpdate(stored, options.confirm, serverTimestamp));
    transaction.update(entryRef, options.entryPatch);
    if (protocolOn) advanceDayCounters(transaction, stored!.restaurant, [stored!.date], { checked: false });
  });
}

/**
 * Deletes a dashboard placeholder hold that was never completed, only while
 * it is still untouched (isUntouchedPlaceholder) and `keep` does not hold
 * it. Resolves whether it did.
 */
export async function deletePlaceholderIfUntouched(
  id: string,
  /** The guest the placeholder was written with, and the written copy when the caller has it. */
  placeholder: { guest: Record<string, any>; written?: Record<string, any> },
  keep: (stored: Record<string, any>) => boolean = () => false
) {
  const ref = db.collection('requestsV03').doc(id);
  return db.runTransaction(async (transaction) => {
    const stored = (await transaction.get(ref)).data();
    if (!isUntouchedPlaceholder(stored, placeholder) || keep(stored!)) return false;
    transaction.delete(ref);
    return true;
  });
}

/** Slot and table fields the reservation editor shows and may change. */
export const EDITOR_FIELDS = [
  'date',
  'time',
  'startTimeInBlocks',
  'endTimeInBlocks',
  'guests',
  'occassion',
  'tables',
  'tableStr',
  'space',
  'reservationLength',
] as const;

/**
 * The editor's save as a three-way patch: only the fields the form changed
 * since it was opened (`baseline`) are written, so whatever another writer
 * (the API, a colleague) changed meanwhile stays. The changed fields must
 * still hold their opening values; a change of seats, time or tables also
 * requires every capacity field to be unchanged, so a placement checked for
 * one day is never attached to a booking someone moved.
 */
export function editorPatch(baseline: Patch, form: Patch) {
  const changed = EDITOR_FIELDS.filter((field) => field in form && !same(form[field], baseline[field]));
  const patch: Patch = Object.fromEntries(changed.map((field) => [field, form[field]]));
  const guarded = touchesCapacity(patch)
    ? Array.from(new Set([...changed, ...EDITOR_FIELDS.filter((field) => CAPACITY.includes(field) && field in baseline)]))
    : changed;
  const expected: Patch = Object.fromEntries(guarded.map((field) => [field, baseline[field] ?? null]));
  return { patch, expected };
}

/**
 * Saves the reservation editor: the three-way patch plus the given extra
 * fields (bookkeeping, custom data, edited comments). ReservationChanged when
 * the target moved on since the form opened, PlacementOutdated when the day's
 * availability changed for a checked placement.
 */
export async function saveEditedReservation(options: {
  id: string;
  /** Absent for an editor not opened from a stored reservation: then every shown field is written. */
  baseline?: Patch;
  form: Patch;
  extra: Patch;
  placement: Placement;
}) {
  const { patch, expected } = options.baseline
    ? editorPatch(options.baseline, options.form)
    : { patch: options.form, expected: undefined };
  const applied = await updateReservation(
    options.id,
    { ...patch, ...options.extra },
    expected,
    // Only a change of the slot itself is a placement.
    touchesCapacity(patch) ? options.placement : undefined
  );
  if (!applied) throw new ReservationChanged();
  return patch;
}


/**
 * Seat-affecting writes to reservations (new bookings, walk-ins, table and
 * time changes, table blocks, reseating). Restaurants running exclusive
 * offers accept these only through the API's allocation fence (Firestore
 * rules refuse them from the client); everyone else keeps writing directly.
 */
export type ReservationWrite =
  | { op: 'set'; id?: string; data: Record<string, any>; reusableOnly?: boolean }
  | { op: 'update'; id: string; data: Record<string, any>; expected?: Record<string, unknown> }
  | { op: 'delete'; id: string };

const PILOT_TTL_MS = 60_000;
const pilotCache = new Map<string, { value: boolean; until: number }>();

/**
 * Whether the restaurant runs exclusive offers. If the setting cannot be
 * read, the direct path is used: the Firestore rules still refuse seat
 * changes in a pilot restaurant, so the error shows instead of a bypass.
 */
export async function usesAllocationApi(restaurantId: string) {
  const cached = pilotCache.get(restaurantId);
  if (cached && cached.until > Date.now()) return cached.value;
  try {
    const doc = await db.collection('restaurants').doc(restaurantId).collection('settings').doc('visitWishes').get();
    const value = doc.data()?.exclusiveOffers === true;
    pilotCache.set(restaurantId, { value, until: Date.now() + PILOT_TTL_MS });
    return value;
  } catch {
    return false;
  }
}

/** JSON carries no Firestore transforms; the API understands these markers. */
export function encodeForApi(value: any): any {
  if (value instanceof FieldValue) {
    if (value.isEqual(FieldValue.delete())) return { __fieldValue: 'delete' };
    if (value.isEqual(FieldValue.serverTimestamp())) return { __fieldValue: 'serverTimestamp' };
    throw new Error('This field transform cannot be sent to the allocation API.');
  }
  if (Array.isArray(value)) return value.map(encodeForApi);
  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, encodeForApi(entry)])
    );
  }
  return value;
}

/**
 * The API's answer to a refused write (e.g. `EXCLUSIVE_CLAIM_CONFLICT`,
 * `WAITINGLIST_ENTRY_CHANGED`), for the caller's message to staff.
 */
export const allocationErrorCode = (error: any): string | null => error?.response?.data?.code ?? null;

/** Applies the writes atomically; resolves with the document ids (new ones included). */
export async function writeReservations(restaurantId: string, writes: ReservationWrite[], dates: string[] = []): Promise<string[]> {
  if (await usesAllocationApi(restaurantId)) return postWrites(restaurantId, { writes });

  const collection = db.collection('requestsV03');
  const batch = db.batch();
  const ids = writes.map((write) => {
    const ref = write.op === 'set' && !write.id ? collection.doc() : collection.doc(write.id);
    if (write.op === 'set') batch.set(ref, write.data);
    else if (write.op === 'update') batch.update(ref, write.data);
    else batch.delete(ref);
    return ref.id;
  });
  if (dates.length && await capacityProtocolOn(restaurantId)) advanceDayCounters(batch, restaurantId, dates);
  await batch.commit();
  return ids;
}

async function postWrites(restaurantId: string, body: { writes: ReservationWrite[]; [key: string]: unknown }) {
  const { data } = (await server.post(`/v03/allocations/${encodeURIComponent(restaurantId)}`, {
    ...Object.fromEntries(Object.entries(body).filter(([key]) => key !== 'writes')),
    guardedWrites: body.writes.map((write) => ('data' in write ? { ...write, data: encodeForApi(write.data) } : write)),
  })) as { data: { ids: string[] } };
  return data.ids;
}

/**
 * Pilot restaurants: creates a waiting-list request's reservation (same id)
 * and marks the request accepted in one API transaction.
 */
export const acceptWaitinglistEntryViaApi = (
  restaurantId: string,
  entryId: string,
  reservation: Record<string, any>,
  updatedBy?: string
) =>
  postWrites(restaurantId, {
    writes: [{ op: 'set', id: entryId, data: reservation }],
    acceptWaitinglistEntry: { id: entryId, ...(updatedBy ? { updatedBy } : {}) },
  });

export async function acceptVisitWishReservationViaApi(
  restaurantId: string,
  wishId: string,
  reservationId: string,
  reservation: Record<string, any>,
  placement?: Placement
) {
  try {
    const [id] = await postWrites(restaurantId, {
      writes: [{ op: 'set', id: reservationId, data: reservation, reusableOnly: true }],
      acceptVisitWish: { id: wishId },
      ...(placement ? { placement } : {}),
    });
    if (id !== reservationId) throw new Error('Die vorbereitete Reservierung konnte nicht zugeordnet werden.');
    return id;
  } catch (error) {
    const code = allocationErrorCode(error);
    if (code === 'PLACEMENT_OUTDATED') throw new PlacementOutdated();
    if (code === 'RESERVATION_CHANGED') throw new ReservationChanged();
    throw error;
  }
}

export const abandonVisitWishReservationViaApi = (restaurantId: string, wishId: string, reservationId: string) =>
  postWrites(restaurantId, {
    writes: [{ op: 'update', id: reservationId, data: { status: 'failed', validTill: 0 } }],
    abandonVisitWish: { id: wishId },
  });

/** Fields that decide what a reservation occupies (same list as the Firestore rules). */
const SEAT_FIELDS = new Set([
  'tables',
  'tableStr',
  'space',
  'date',
  'time',
  'startTimeInBlocks',
  'endTimeInBlocks',
  'reservationLength',
  'guests',
  'validTill',
  'blocked',
  'status',
  'deleted',
]);

/** Whether an update changes seating; releasing a seat (status failed) does not need the API. */
export const touchesSeat = (data: Record<string, any>) =>
  data.status !== 'failed' && Object.keys(data).some((key) => SEAT_FIELDS.has(key.split('.')[0]));

/**
 * A reservation reference whose updates take the right path: seat changes
 * through `writeReservations`, everything else (notes, flags, custom fields,
 * releases) directly as before.
 */
export function seatAwareRef(restaurantId: string, id: string) {
  const raw = db.collection('requestsV03').doc(id);
  return {
    raw,
    id,
    update: (data: Record<string, any>) => updateReservation(id, data).then(() => undefined),
  };
}
